import { useState } from 'preact/hooks';
import { pickLocale, type CheckoutConfigDto } from './api.js';

type MethodTile = CheckoutConfigDto['strategy']['methodEligibility'][number];
type ProcessingTone = NonNullable<NonNullable<MethodTile['processingCallout']>['tone']>;
type Incentive = CheckoutConfigDto['strategy']['incentives'][number];

/**
 * Visual treatment for the per-tile incentive badges. Keyed by incentive id
 * so the same operator config drives both the admin preview and the live
 * checkout. Unknown ids fall back to a neutral chip.
 */
const INCENTIVE_BADGE: Record<
  string,
  { label: string; tone: 'success' | 'info' | 'warning'; icon?: string }
> = {
  recommended_highlight: { label: 'Recommended', tone: 'success', icon: 'star' },
  no_fee_badge: { label: 'No fee', tone: 'success', icon: 'savings' },
  fast_checkout_timer: { label: 'Fastest', tone: 'info', icon: 'bolt' },
  savings_callout: { label: 'Save', tone: 'warning', icon: 'sell' },
};

const BADGE_TONE: Record<'success' | 'info' | 'warning', string> = {
  success: 'bg-success-container text-on-success-container',
  info: 'bg-secondary-container text-on-secondary-container',
  warning: 'bg-warning-container text-on-warning-container',
};

/**
 * Pick the incentives that apply to a specific method tile. Empty / missing
 * `appliesTo` is treated as "all methods" so operators can ship a global
 * promotion without per-method config.
 */
function incentivesFor(method: string, all: Incentive[] | undefined): Incentive[] {
  if (!all || all.length === 0) return [];
  return all.filter((i) => {
    if (!i.enabled) return false;
    if (!i.appliesTo || i.appliesTo.length === 0) return true;
    return i.appliesTo.includes(method);
  });
}

/**
 * Tone → visual mapping for processing callout chips. Each tone keys to:
 *   - a tile-embedded chip palette (background + accent border-left)
 *   - a default icon
 * Mapping is shared between the in-tile chip and the contextual band's
 * selected-method pill so shoppers learn the green=fast / orange=slow code.
 */
const TONE_STYLE: Record<ProcessingTone, { chip: string; pill: string; icon: string }> = {
  success: {
    chip: 'bg-success-container/70 text-on-success-container border-l-success',
    pill: 'bg-success-container text-on-success-container',
    icon: 'bolt',
  },
  info: {
    chip: 'bg-secondary-container/70 text-on-secondary-container border-l-secondary',
    pill: 'bg-secondary-container text-on-secondary-container',
    icon: 'schedule',
  },
  warning: {
    chip: 'bg-warning-container/70 text-on-warning-container border-l-warning',
    pill: 'bg-warning-container text-on-warning-container',
    icon: 'pending',
  },
  neutral: {
    chip: 'bg-surface-container text-on-surface-variant border-l-outline-variant',
    pill: 'bg-surface-container text-on-surface-variant',
    icon: 'info',
  },
};

export function toneStyleFor(tone: ProcessingTone | undefined) {
  return TONE_STYLE[tone ?? 'info'];
}

/**
 * Payment-method picker — renders cheap (primary-tier) tiles big and
 * default-visible, then collapses expensive (secondary-tier) tiles behind
 * a single "More ways to pay" expander. Operators tune the tiering per
 * tenant in the admin builder; this component has zero hardcoded ordering.
 */
export function MethodTiles(props: {
  methods: MethodTile[];
  /**
   * Strategy-level visual incentives. The picker filters them down to the
   * subset that applies to each tile. Optional — when omitted, no badges.
   */
  incentives?: Incentive[];
  locale: string;
  selected: string | null;
  onSelect: (paymentMethod: string) => void;
}) {
  const enabled = props.methods.filter((m) => m.enabled && m.tier !== 'hidden');
  const primary = enabled.filter((m) => (m.tier ?? 'primary') === 'primary');
  const secondary = enabled.filter((m) => m.tier === 'secondary');
  const [expanded, setExpanded] = useState(false);

  if (enabled.length === 0) return null;

  // Picking a primary tile re-collapses the secondary tier — the shopper
  // signalled they've made a choice from the default-visible options, so
  // the expander resets to its default state.
  function pickPrimary(method: string) {
    props.onSelect(method);
    setExpanded(false);
  }

  return (
    <div class="space-y-2">
      <p class="upg-eyebrow text-on-primary-container">Choose how to pay</p>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5 lg:gap-3">
        {primary.map((m) => (
          <Tile
            key={m.paymentMethod}
            m={m}
            locale={props.locale}
            selected={props.selected === m.paymentMethod}
            incentives={incentivesFor(m.paymentMethod, props.incentives)}
            onClick={() => pickPrimary(m.paymentMethod)}
          />
        ))}
      </div>
      {secondary.length > 0 && (
        <div class="pt-1">
          {!expanded ? (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              class="w-full text-xs uppercase tracking-widest font-semibold text-on-surface-variant hover:text-on-surface py-2 border border-dashed border-outline-variant rounded"
            >
              + More ways to pay ({secondary.length})
            </button>
          ) : (
            <div class="space-y-2">
              {secondary.map((m) => (
                <Tile
                  key={m.paymentMethod}
                  m={m}
                  locale={props.locale}
                  selected={props.selected === m.paymentMethod}
                  incentives={incentivesFor(m.paymentMethod, props.incentives)}
                  onClick={() => props.onSelect(m.paymentMethod)}
                  compact
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Tile(props: {
  m: MethodTile;
  locale: string;
  selected: boolean;
  incentives: Incentive[];
  onClick: () => void;
  compact?: boolean;
}) {
  const { m } = props;
  const label = pickLocale(m.label, props.locale) || prettyMethod(m.paymentMethod);
  const caption = pickLocale(m.caption, props.locale);
  const callout = m.processingCallout;
  const calloutText = callout ? pickLocale(callout.text, props.locale) : '';
  const tone = toneStyleFor(callout?.tone);
  const calloutIcon = callout?.icon || tone.icon;
  return (
    <button
      type="button"
      onClick={props.onClick}
      class={`relative text-left w-full ${
        props.compact ? 'p-2.5 sm:p-3' : 'p-3 sm:p-4'
      } border rounded-lg transition-colors ${
        props.selected
          ? 'border-primary bg-primary-container/30'
          : 'border-outline-variant hover:border-on-surface bg-surface-container-lowest'
      }`}
    >
      <div class="flex items-start gap-3">
        {m.icon && (
          <span
            class="material-symbols-outlined text-on-surface-variant shrink-0 mt-0.5"
            style={{ fontSize: props.compact ? '18px' : '22px' }}
            aria-hidden="true"
          >
            {m.icon}
          </span>
        )}
        {/* `min-w-0` lets the flex child shrink below its content width;
            without it the label would force horizontal overflow at narrow
            widths even with `break-words`. */}
        <div class="flex-1 min-w-0">
          <div class="flex items-start gap-1.5 flex-wrap">
            {/* Allow long labels (e.g. "Cards (Visa / MC / Amex / Discover)")
                to wrap across two lines rather than ellipsis-clip. */}
            <p
              class={`font-semibold ${
                props.compact ? 'text-body-sm' : 'text-body-md'
              } leading-snug break-words`}
            >
              {label}
            </p>
            {props.incentives.map((inc) => {
              const meta = INCENTIVE_BADGE[inc.id] ?? {
                label: inc.label ?? inc.id,
                tone: 'info' as const,
              };
              return (
                <span
                  key={inc.id}
                  class={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-bold whitespace-nowrap ${
                    BADGE_TONE[meta.tone]
                  }`}
                >
                  {meta.icon && (
                    <span
                      class="material-symbols-outlined"
                      style={{ fontSize: '11px' }}
                      aria-hidden="true"
                    >
                      {meta.icon}
                    </span>
                  )}
                  {inc.label ?? meta.label}
                </span>
              );
            })}
          </div>
          {caption && (
            <p class="text-[11px] text-on-surface-variant mt-0.5 leading-snug break-words">
              {caption}
            </p>
          )}
        </div>
        {props.selected && (
          <span class="material-symbols-outlined text-primary shrink-0 mt-0.5" aria-hidden="true" style={{ fontSize: '20px' }}>
            check_circle
          </span>
        )}
      </div>
      {callout && calloutText && (
        <div
          class={`mt-2 flex items-start gap-1.5 px-2 py-1 rounded border-l-2 text-[11px] leading-snug ${tone.chip}`}
        >
          <span
            class="material-symbols-outlined shrink-0 mt-0.5"
            style={{ fontSize: '14px' }}
            aria-hidden="true"
          >
            {calloutIcon}
          </span>
          {/* `min-w-0` to play nice with flex; `break-words` so long
              processing copy wraps inside the tile rather than ellipsis. */}
          <span class="min-w-0 break-words">{calloutText}</span>
        </div>
      )}
    </button>
  );
}

function prettyMethod(id: string): string {
  return id
    .split(/[_-]/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}
